/**
 * ModuleMaturityBadge — the beta (β) / alpha (α) maturity marker rendered next to
 * a module name. Stable modules render nothing.
 *
 * Companion to ModuleStatusIndicator (which overlays a Greek letter on a module
 * icon); this is the labelled variant for places that show the module name.
 */

import { useTranslation } from 'react-i18next';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import type { ModuleStatus } from '../config/modules.config';

interface ModuleMaturityBadgeProps {
  status: ModuleStatus;
  className?: string;
}

export function ModuleMaturityBadge({ status, className }: ModuleMaturityBadgeProps) {
  const { t } = useTranslation('common');

  if (status === 'stable' || status === 'hidden') {
    return null;
  }

  return (
    <Badge
      variant="outline"
      className={cn(
        'px-1.5 py-0 text-[10px] font-medium',
        status === 'beta' && 'border-info/30 bg-info/10 text-info-strong',
        status === 'alpha' && 'border-warning/30 bg-warning/10 text-warning-strong',
        className
      )}
    >
      {t(`modules.status.${status}`)}
    </Badge>
  );
}
